/**
 * Fleet API fetch functions for the event permalink page.
 *
 * Data contracts from fleet-api Plan 03-01 Task 3:
 *   GET /v1/alerts/{alert_id}          -> AlertDetail
 *   GET /v1/alerts/{alert_id}/related  -> AlertRelated[]
 *
 * Both endpoints are org-scoped: requesting an alert_id from another org
 * returns 404 (never 403, per the Phase 1.5 pen-test invariant). The UI
 * treats 404 as "not found / may belong to another organization."
 */
import { apiFetch, ApiError } from '@/lib/api-client';
import { AlertDetailSchema, AlertRelatedSchema, EntryEventDetailSchema } from './schemas';
import type { AlertDetail, AlertRelated, EntryEventDetail } from './schemas';
import { z } from 'zod';

/**
 * Fetches full alert detail for the event permalink page.
 *
 * Returns null on 404 (cross-org access or non-existent alert_id).
 * Throws ApiError for other non-2xx statuses.
 *
 * @param alertId - Numeric alert primary key from the URL /event/{id}
 * @returns Validated AlertDetail, or null if 404
 */
export async function fetchAlertDetail(alertId: number): Promise<AlertDetail | null> {
  try {
    const raw = await apiFetch<unknown>(`/v1/alerts/${alertId}`);
    return AlertDetailSchema.parse(raw);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return null;
    }
    throw err;
  }
}

/**
 * Fetches related events for the event permalink sidebar.
 *
 * Returns events that share the same fingerprint (past visits) or the same
 * zone during the same hour. Maximum 10 events per fleet-api contract.
 *
 * Returns [] when the alert has no related events or the alert_id is invalid.
 *
 * @param alertId - Numeric alert primary key
 * @returns Validated array of AlertRelated (may be empty)
 */
export async function fetchAlertRelated(alertId: number): Promise<AlertRelated[]> {
  try {
    const raw = await apiFetch<unknown>(`/v1/alerts/${alertId}/related`);
    return z.array(AlertRelatedSchema).parse(raw);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return [];
    }
    throw err;
  }
}

/**
 * Fetches one entry_events_mirror row for the EntryEventDrill page.
 *
 * Plan 12-11 consumer of GET /v1/entry-events/{event_id}. Returns null
 * on 404 so the caller can render the not-found empty state without a
 * try/catch in component code; non-404 errors bubble up so the React
 * Query error path can fire.
 *
 * @param eventId - BigInt cloud-side PK from the URL (/event/entry-<id>)
 * @returns Validated EntryEventDetail or null on 404
 */
export async function fetchEntryEventDetail(
  eventId: number,
): Promise<EntryEventDetail | null> {
  try {
    const raw = await apiFetch<unknown>(`/v1/entry-events/${eventId}`);
    return EntryEventDetailSchema.parse(raw);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return null;
    }
    throw err;
  }
}
